/**
 * Defines the data model-specs, schemas, and specifications for the core chat functionality.
 * Uses `defineModelSpec` to create `ChatSpec` and `ChatMessageSpec`.
 * Imports the base `MessageSchema` and various `MessagePart` types.
 * Imports the `userId` placeholder.
 */

import { z } from 'zod';

import { collectionPath, defineModelSpec, orderBy } from '@elara/firebolt/shared';
import { zFirestampNow } from '@elara/firebolt/zod-types';

import { AgentMetadataSchema } from './schemas/AgentMetadataSchema.js';
import { chatId, chatMessageId, id, userId } from './schemas/zod-types.js';

export const ToolCallStatus = z.enum(['pending', 'executing', 'completed', 'failed']);
export type ToolCallStatus = z.infer<typeof ToolCallStatus>;

export const ToolCallType = z.enum(['tool', 'agent', 'widget']);
export type ToolCallType = z.infer<typeof ToolCallType>;

/**
 * The canonical `ModelSpec` for the tools sub-collection within a message.
 * It defines the path (`chats/{chatId}/messages/{messageId}/tools/{id}`) and the data shape for a tool call document.
 * This subcollection stores detailed information about tool calls for debugging and auditing purposes.
 */
export const ToolCallSpec = defineModelSpec({
  path: collectionPath`chats/${chatId}/messages/${chatMessageId}/tools/${id}`,
  shape: {
    // Tool identification
    toolName: z.string().describe('Name of the tool that was called'),
    toolRef: z.string().optional().describe('Reference ID for this specific tool call'),
    // Request data
    request: z.object({
      input: z.record(z.string(), z.unknown()).optional().describe('The input parameters sent to the tool'),
      timestamp: zFirestampNow().describe('When the tool was requested'),
    }),
    // Response data
    response: z
      .object({
        output: z.unknown().optional().describe('The output returned from the tool'),
        error: z.string().optional().describe('Error message if the tool call failed'),
        timestamp: zFirestampNow().describe('When the tool responded'),
        duration: z.number().optional().describe('Time taken to execute the tool in milliseconds'),
      })
      .optional(),
    // Metadata
    status: ToolCallStatus.default('pending'),
    userId: userId.describe('User for whom the tool call was initiated'),
    chatId: z.string().describe('ID of the chat where this tool call occurred'),
    messageId: z.string().describe('ID of the message that initiated this tool call'),
    callType: ToolCallType.default('tool'),
    agent: AgentMetadataSchema.optional().describe('Agent metadata when the call represents an agent invocation'),
  },
  constraints: [orderBy('createdAt')],
});
export type ToolCallSpec = typeof ToolCallSpec;
